import { ReactElement, useContext, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { DealmakerActor, DealmakerCatalogResult, DealmakerDetail, getDealmakerCatalog, getDealmakerDeal, recordDealmakerVisit } from "../../Services/DealmakerCatalog";
import AuthContext from "../../store/AuthContext";
import { IPerson } from "../../interfaces/models/IPerson";
import "../../assets/css/dealmaker.css";
import DealmakerContact from "./components/DealmakerContact";
import DealmakerIntro from "./components/DealmakerIntro";
import DealmakerList from "./components/DealmakerList";
import DealmakerOverview from "./components/DealmakerOverview";

function sessionTokenPresent(): boolean {
  if (sessionStorage.getItem("token")) {
    return true;
  }
  return document.cookie.split(";").some((part) => part.trim().startsWith("token="));
}

function phoneText(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (!value || typeof value !== "object") {
    return "";
  }
  const row = value as { code?: unknown; phone?: unknown };
  const code = typeof row.code === "string" ? row.code.trim() : "";
  const phone = typeof row.phone === "string" ? row.phone.trim() : "";
  return [code, phone].filter((part) => part.length > 0).join(" ");
}

function actorFromPerson(person: IPerson): DealmakerActor {
  const title = person.listTitle || person.unlistedTitle || String(person.title || "");
  return {
    contactId: String(person.legacyId || ""),
    companyId: String(person.firmLegacyId || ""),
    name: `${person.name || ""} ${person.lastName || ""}`.trim(),
    email: person.firmEmail || "",
    phone: phoneText(person.directPhoneNumber) || phoneText(person.phoneNumber),
    firmName: person.firmName || "",
    title,
  };
}

function catalogQuery(params: URLSearchParams): Record<string, string> {
  const query: Record<string, string> = {};
  ["words", "firmId", "jurisdiction", "regionId", "industryId", "dealTypeId", "sort", "page", "archivedPage"].forEach((key) => {
    const value = params.get(key);
    if (value) {
      query[key] = value;
    }
  });
  return query;
}

export default function DealmakerPage(): ReactElement {
  const [params] = useSearchParams();
  const { loggedPerson } = useContext(AuthContext);
  const action = params.get("action") || "";
  const search = params.toString();
  const isDetail = action === "view" || action === "info";
  const [catalog, setCatalog] = useState<DealmakerCatalogResult | null>(null);
  const [deal, setDeal] = useState<DealmakerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isDetail && !sessionTokenPresent()) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    const current = new URLSearchParams(search);
    const load = isDetail
      ? getDealmakerDeal(current.get("dealid") || "", current.get("view") === "1").then((row) => {
        if (!cancelled) {
          setDeal(row);
        }
      })
      : getDealmakerCatalog(catalogQuery(current)).then((row) => {
        if (!cancelled) {
          setCatalog(row);
        }
      });
    load.catch(() => {
      if (!cancelled) {
        setError("The deal catalog could not be loaded.");
      }
    }).finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isDetail, search]);

  useEffect(() => {
    if (isDetail || !loggedPerson?.legacyId) {
      return;
    }
    void recordDealmakerVisit(actorFromPerson(loggedPerson)).catch(() => undefined);
  }, [isDetail, loggedPerson]);

  if (!isDetail && !sessionTokenPresent()) {
    return <Navigate to="/login?legacyUrl=/dealmaker" replace />;
  }
  if (action === "info" && !sessionTokenPresent()) {
    return <Navigate to="/login?legacyUrl=/dealmaker" replace />;
  }

  const actor = loggedPerson ? actorFromPerson(loggedPerson) : null;

  return (
    <div className="dealmaker-sheet">
      <DealmakerIntro />
      {isDetail && loading ? <p>Loading deals...</p> : null}
      {isDetail && error ? <p className="dealmaker-error">{error}</p> : null}
      {action === "view" && deal ? <DealmakerOverview deal={deal} /> : null}
      {action === "info" && deal && actor ? <DealmakerContact deal={deal} actor={actor} /> : null}
      {action === "info" && deal && !actor ? <p>Sign in to contact the firm.</p> : null}
      {!isDetail ? <DealmakerList catalog={catalog} loading={loading} error={error} /> : null}
    </div>
  );
}
